import { useEffect, useState } from 'react';

// material-ui
import { Button, Grid, Typography, Paper, Avatar, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { gridSpacing } from 'store/constant';
import { getApi } from 'views/services/api';

import TotalRoomsCard from './TotalRooms';
import TotalCustomersCard from './TotalCustomers';
import FoodItems from './TotalFoodItems';
import TotalReservationCard from './TotalReseravtions';

import SingleBedIcon from '@mui/icons-material/SingleBed';
import KingBedIcon from '@mui/icons-material/KingBed';

import ReserveRoom from 'views/Room/ReserveRoom';
import MultiRoomBooking from 'views/Room/MultiRoomBooking';
import { useNavigate } from 'react-router';
import Iconify from 'ui-component/iconify';

const RoomTypeIcons = {
  single: <SingleBedIcon sx={{ fontSize: '2.2rem' }} />,
  double: <KingBedIcon sx={{ fontSize: '2.2rem' }} />,
  triple: <KingBedIcon sx={{ fontSize: '2.2rem' }} />,
  family: <KingBedIcon sx={{ fontSize: '2.2rem' }} />
};

const MainDashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [isLoading, setLoading] = useState(true);
  const [openReserveRoom, setOpenReserveRoom] = useState(false);
  const [roomPropsData, setRoomPropsData] = useState([]);
  const [openMultiRoomBooking, setOpenMultiRoomBooking] = useState(false);

  const [roomData, setroomData] = useState([]);
  const [reservationData, setreservationData] = useState([]);
  const [customerData, setcustomerData] = useState([]);
  const [foodItemData, setitemsData] = useState([]);

  const hotel = JSON.parse(localStorage.getItem('hotelData'));

  useEffect(() => {
    setLoading(false);
  }, []);

  /* ================= FETCH (UNCHANGED) ================= */

  const fetchroomData = async () => {
    const response = await getApi(`api/room/viewallrooms/${hotel?.hotelId}`);
    setroomData(response?.data);
  };

  const fetchReservationData = async () => {
    const response = await getApi(
      `api/reservation/viewallactivereservations/${hotel?.hotelId}`
    );
    setreservationData(response?.data?.reservationData);
  };

  const fetchcustomerData = async () => {
    const response = await getApi(
      `api/customer/viewallcustomer/${hotel?.hotelId}`
    );
    setcustomerData(response?.data?.customerData);
  };

  const fetchitemsData = async () => {
    const response = await getApi(
      `api/restaurant/viewallitems/${hotel?.hotelId}`
    );
    setitemsData(response?.data?.restaurantData);
  };

  useEffect(() => {
    fetchroomData();
    fetchReservationData();
    fetchcustomerData();
    fetchitemsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openReserveRoom]);

  /* ================= HANDLERS (UNCHANGED) ================= */

  const fetchReservationIdForActiveRooms = async (roomNo) => {
    const res = await getApi(`api/room/activroomreservationid/${roomNo}`);
    navigate(`/dashboard/reservation/view/${res?.data[0]?.reservationId}`);
  };

  const handleRoomClick = (roominfo) => {
    if (roominfo.bookingStatus === 'active') {
      fetchReservationIdForActiveRooms(roominfo.roomNo);
    } else {
      setOpenReserveRoom(true);
    }
    setRoomPropsData(roominfo);
  };

  return (
    <>
      <ReserveRoom
        open={openReserveRoom}
        handleClose={() => setOpenReserveRoom(false)}
        roomDataByProps={roomPropsData}
      />

      <MultiRoomBooking
        open={openMultiRoomBooking}
        handleClose={() => setOpenMultiRoomBooking(false)}
        onBookingSuccess={fetchroomData}
      />

      <Grid container spacing={gridSpacing}>

        {/* ================= TOP CARDS (FRESH DESIGN) ================= */}
        <Grid item xs={12}>
          <Grid container spacing={gridSpacing}>
            {[
              {
                component: (
                  <TotalRoomsCard isLoading={isLoading} roomData={roomData} />
                ),
                path: '/dashboard/roommanagement'
              },
              {
                component: (
                  <TotalCustomersCard
                    isLoading={isLoading}
                    customerData={customerData}
                  />
                ),
                path: '/dashboard/customers'
              },
              {
                component: (
                  <FoodItems
                    isLoading={isLoading}
                    foodItemData={foodItemData}
                  />
                ),
                path: '/dashboard/restaurant'
              },
              {
                component: (
                  <TotalReservationCard
                    isLoading={isLoading}
                    reservationData={reservationData}
                  />
                ),
                path: '/dashboard/reservation'
              }
            ].map((item, i) => (
              <Grid
                key={i}
                item
                lg={3}
                md={6}
                sm={6}
                xs={12}
                onClick={() => navigate(item.path)}
                sx={{ cursor: 'pointer' }}
              >
                <Paper
                  sx={{
                    borderRadius: 3,
                    p: 1,
                    background:
                      'linear-gradient(135deg, #f9fafb, #ffffff)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(0,0,0,0.12)'
                    }
                  }}
                >
                  {item.component}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* ================= ROOMS ================= */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              Rooms ({roomData?.length || 0})
            </Typography>

            <Button
              variant="contained"
              startIcon={<Iconify icon="mdi:bed-multiple" />}
              onClick={() => setOpenMultiRoomBooking(true)}
              sx={{
                backgroundColor: '#4caf50',
                '&:hover': { backgroundColor: '#388e3c' }
              }}
            >
              Multi-Room Booking
            </Button>
          </Box>

          <Grid container spacing={gridSpacing}>
            {roomData?.map((room) => {
              const isActive = room.bookingStatus === "true";

              return (
                <Grid
                  item
                  key={room.id}
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  onClick={() => handleRoomClick(room)}
                >
                  <Paper
                    elevation={3}
                    sx={{
                      p: 3,
                      borderRadius: 3,
                      cursor: 'pointer',
                      backgroundColor: isActive ? '#fff5f5' : '#f9fdfb',
                      border: isActive
                        ? '1px solid #f44336'
                        : '1px solid #4caf50',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 14px 28px rgba(0,0,0,0.12)'
                      }
                    }}
                  >
                    <Box textAlign="center">
                      <Avatar
                        sx={{
                          mx: 'auto',
                          mb: 1.5,
                          width: 56,
                          height: 56,
                          backgroundColor: '#fff',
                          color: isActive ? '#f44336' : '#4caf50'
                        }}
                      >
                        {RoomTypeIcons[room.roomType.toLowerCase()]}
                      </Avatar>

                      <Typography fontWeight={600}>
                        {room.roomType}
                      </Typography>

                      <Box
                        sx={{
                          mt: 1,
                          px: 2,
                          py: 0.5,
                          borderRadius: 2,
                          backgroundColor: isActive
                            ? '#fdecea'
                            : '#e8f5e9',
                          color: isActive
                            ? '#d32f2f'
                            : '#2e7d32',
                          fontWeight: 700,
                          display: 'inline-block'
                        }}
                      >
                        Room {room.roomNo}
                      </Box>

                      <Typography
                        variant="caption"
                        display="block"
                        mt={1}
                        fontWeight={600}
                        color={isActive ? 'error.main' : 'success.main'}
                      >
                        {isActive ? 'Occupied' : 'Available'}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default MainDashboard;
